import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { API } from 'api';
import {
  logUnexpectedErrorAndNotifyUser,
  logUnexpectedErrorAndNotifyUserWithTryAgain,
} from 'shared/handleUnexpectedError';
import { useSSORequired } from 'features/sso/hooks/useSSORequired';
import { useState } from 'react';
import { HttpStatusCode } from 'axios';
import { LogoWithTagline } from 'components/LogoWithTagline';
import { useQueryParams } from 'hooks/useQueryParams';

type FormValues = {
  email: string;
};

export const ResetPasswordForm = (): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation();
  const [queryParams] = useQueryParams<{ email?: string }>();
  const [ssoUsername, setSSOUsername] = useState('');

  const { ssoRequired, loginUrl, isLoading } = useSSORequired(ssoUsername);

  const validationSchema = Yup.object({
    email: Yup.string()
      .required(t('resetPassword.inputs.email.required'))
      .email(t('resetPassword.inputs.email.error')),
  });

  const { control, handleSubmit, setError } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: queryParams.email ? decodeURIComponent(queryParams.email) : '',
    },
    mode: 'onBlur',
  });

  const { isPending, mutate } = useMutation({
    mutationFn: (newLogin: { email: string }) =>
      API.post('auth/password/reset/', newLogin),
  });

  const handleResetPassword: SubmitHandler<FormValues> = (values) => {
    mutate(values, {
      onSuccess: () => {
        history.push(
          `/reset_password_confirmation?email=${encodeURIComponent(values.email)}`,
        );
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (error.response?.status !== HttpStatusCode.BadRequest) {
          logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
          return;
        }

        const emailError = error.response.data.email;
        if (emailError) {
          const errorMessage = emailError.join('\n');
          setError('email', { type: 'custom', message: errorMessage });
          return;
        }
        // Generic error message
        logUnexpectedErrorAndNotifyUser(t, { error });
      },
    });
  };

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit(handleResetPassword)}
      noValidate
    >
      <Stack spacing={6}>
        <LogoWithTagline />

        <Stack spacing={4}>
          <Stack spacing={2}>
            <Typography>{t('resetPassword.description')}</Typography>

            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, onChange, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-email"
                    label={t('resetPassword.inputs.email.label')}
                    fullWidth
                    type="email"
                    {...muiFieldProps}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    onChange={(e) => {
                      onChange(e);
                      setSSOUsername(e.target.value);
                    }}
                    helperText={fieldState.error?.message}
                    required
                  />
                );
              }}
            />
          </Stack>

          {ssoRequired ? (
            <Button type="submit" fullWidth size="large" href={loginUrl}>
              {t('login.buttons.loginWithSSO')}
            </Button>
          ) : (
            <Button
              disabled={isPending || isLoading}
              type="submit"
              fullWidth
              size="large"
            >
              {isPending ? (
                <CircularProgress size={24} color="secondary" />
              ) : (
                t('resetPassword.button')
              )}
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );
};
